import React from 'react';
import { arenaAudio } from '../../../services/ArenaAudioService';

export type ArenaTableColor = 'default' | 'blue' | 'red' | 'pink' | 'gold';
export type ArenaTableRail = 'bronze' | 'wood' | 'dark' | 'floral' | 'gold';
export type SouthHandFontSize = 'regular' | 'medium' | 'extra-large';

let currentTableColor: ArenaTableColor = 'default';
if (typeof localStorage !== 'undefined') {
  try {
    const saved = localStorage.getItem('atl_table_color');
    if (saved === 'blue' || saved === 'red' || saved === 'pink' || saved === 'gold') {
      currentTableColor = saved;
    }
  } catch {}
}

const tableColorListeners = new Set<(color: ArenaTableColor) => void>();

function notifyTableColorChange() {
  try {
    const saved = localStorage.getItem('atl_table_color');
    if (saved === 'blue' || saved === 'red' || saved === 'pink' || saved === 'gold') {
      currentTableColor = saved;
    } else {
      currentTableColor = 'default';
    }
  } catch {}
  tableColorListeners.forEach(l => l(currentTableColor));
}

let isGlobalTableColorListening = false;
function ensureTableColorListener() {
  if (isGlobalTableColorListening || typeof window === 'undefined') return;
  isGlobalTableColorListening = true;
  window.addEventListener('atl_table_color_change', notifyTableColorChange);
  window.addEventListener('storage', notifyTableColorChange);
}

const VALID_RAILS: ArenaTableRail[] = ['wood', 'gold', 'bronze', 'dark', 'floral'];

let currentTableRail: ArenaTableRail = 'wood';
if (typeof localStorage !== 'undefined') {
  try {
    const saved = localStorage.getItem('atl_table_rail') as ArenaTableRail;
    if (VALID_RAILS.includes(saved)) {
      currentTableRail = saved;
    }
  } catch {}
}

const tableRailListeners = new Set<(rail: ArenaTableRail) => void>();

function notifyTableRailChange() {
  try {
    const saved = localStorage.getItem('atl_table_rail') as ArenaTableRail;
    if (VALID_RAILS.includes(saved)) {
      currentTableRail = saved;
    } else {
      currentTableRail = 'wood';
    }
  } catch {}
  tableRailListeners.forEach(l => l(currentTableRail));
}

let isGlobalTableRailListening = false;
function ensureTableRailListener() {
  if (isGlobalTableRailListening || typeof window === 'undefined') return;
  isGlobalTableRailListening = true;
  window.addEventListener('atl_table_rail_change', notifyTableRailChange);
  window.addEventListener('storage', notifyTableRailChange);
}

let currentShowBooksToSet: boolean = false;
if (typeof localStorage !== 'undefined') {
  try {
    currentShowBooksToSet = localStorage.getItem('atl_show_books_to_set') === 'true';
  } catch {}
}

const booksToSetListeners = new Set<(show: boolean) => void>();

function notifyBooksToSetChange() {
  try {
    currentShowBooksToSet = localStorage.getItem('atl_show_books_to_set') === 'true';
  } catch {}
  booksToSetListeners.forEach(l => l(currentShowBooksToSet));
}

let isGlobalBooksToSetListening = false;
function ensureBooksToSetListener() {
  if (isGlobalBooksToSetListening || typeof window === 'undefined') return;
  isGlobalBooksToSetListening = true;
  window.addEventListener('atl_show_books_to_set_change', notifyBooksToSetChange);
  window.addEventListener('storage', notifyBooksToSetChange);
}

let currentShowCardHints: boolean = true;
if (typeof localStorage !== 'undefined') {
  try {
    const saved = localStorage.getItem('atl_card_hints');
    if (saved !== null) {
      currentShowCardHints = saved !== 'false';
    }
  } catch {}
}

const cardHintsListeners = new Set<(show: boolean) => void>();

function notifyCardHintsChange() {
  try {
    const saved = localStorage.getItem('atl_card_hints');
    if (saved !== null) {
      currentShowCardHints = saved !== 'false';
    } else {
      currentShowCardHints = true;
    }
  } catch {}
  cardHintsListeners.forEach(l => l(currentShowCardHints));
}

let isGlobalCardHintsListening = false;
function ensureCardHintsListener() {
  if (isGlobalCardHintsListening || typeof window === 'undefined') return;
  isGlobalCardHintsListening = true;
  window.addEventListener('atl_card_hints_change', notifyCardHintsChange);
  window.addEventListener('storage', notifyCardHintsChange);
}

export function useCardHints(): boolean {
  const [showHints, setShowHints] = React.useState<boolean>(currentShowCardHints);

  React.useEffect(() => {
    ensureCardHintsListener();
    cardHintsListeners.add(setShowHints);
    return () => {
      cardHintsListeners.delete(setShowHints);
    };
  }, []);

  return showHints;
}

const VALID_FONT_SIZES: SouthHandFontSize[] = ['regular', 'medium', 'extra-large'];

let currentSouthHandFontSize: SouthHandFontSize = 'medium';
if (typeof localStorage !== 'undefined') {
  try {
    const saved = localStorage.getItem('atl_south_hand_font_size') as SouthHandFontSize;
    if (VALID_FONT_SIZES.includes(saved)) {
      currentSouthHandFontSize = saved;
    }
  } catch {}
}

const southHandFontSizeListeners = new Set<(size: SouthHandFontSize) => void>();

function notifySouthHandFontSizeChange() {
  try {
    const saved = localStorage.getItem('atl_south_hand_font_size') as SouthHandFontSize;
    if (VALID_FONT_SIZES.includes(saved)) {
      currentSouthHandFontSize = saved;
    } else {
      currentSouthHandFontSize = 'medium';
    }
  } catch {}
  southHandFontSizeListeners.forEach(l => l(currentSouthHandFontSize));
}

let isGlobalSouthHandFontSizeListening = false;
function ensureSouthHandFontSizeListener() {
  if (isGlobalSouthHandFontSizeListening || typeof window === 'undefined') return;
  isGlobalSouthHandFontSizeListening = true;
  window.addEventListener('atl_south_hand_font_size_change', notifySouthHandFontSizeChange);
  window.addEventListener('storage', notifySouthHandFontSizeChange);
}

export function useSouthHandFontSize(): SouthHandFontSize {
  const [fontSize, setFontSize] = React.useState<SouthHandFontSize>(currentSouthHandFontSize);

  React.useEffect(() => {
    ensureSouthHandFontSizeListener();
    southHandFontSizeListeners.add(setFontSize);
    return () => {
      southHandFontSizeListeners.delete(setFontSize);
    };
  }, []);

  return fontSize;
}

export function useArenaTableSettings() {
  const [tableColor, setTableColor] = React.useState<ArenaTableColor>(currentTableColor);
  const [tableRail, setTableRail] = React.useState<ArenaTableRail>(currentTableRail);
  const [showBooksToSet, setShowBooksToSet] = React.useState<boolean>(currentShowBooksToSet);
  const [southHandFontSize, setSouthHandFontSize] = React.useState<SouthHandFontSize>(currentSouthHandFontSize);

  React.useEffect(() => {
    ensureTableColorListener();
    tableColorListeners.add(setTableColor);
    return () => {
      tableColorListeners.delete(setTableColor);
    };
  }, []);

  React.useEffect(() => {
    ensureTableRailListener();
    tableRailListeners.add(setTableRail);
    return () => {
      tableRailListeners.delete(setTableRail);
    };
  }, []);

  React.useEffect(() => {
    ensureBooksToSetListener();
    booksToSetListeners.add(setShowBooksToSet);
    return () => {
      booksToSetListeners.delete(setShowBooksToSet);
    };
  }, []);

  React.useEffect(() => {
    ensureSouthHandFontSizeListener();
    southHandFontSizeListeners.add(setSouthHandFontSize);
    return () => {
      southHandFontSizeListeners.delete(setSouthHandFontSize);
    };
  }, []);

  const handleCycleTableColor = React.useCallback(() => {
    try {
      const nextMap: Record<ArenaTableColor, ArenaTableColor> = {
        default: 'blue',
        blue: 'red',
        red: 'pink',
        pink: 'gold',
        gold: 'default',
      };
      const next = nextMap[currentTableColor] || 'default';
      currentTableColor = next;
      localStorage.setItem('atl_table_color', next);
      window.dispatchEvent(new Event('atl_table_color_change'));
      arenaAudio.playCardSelect();
    } catch {}
  }, []);

  const handleToggleTableRail = React.useCallback(() => {
    try {
      const nextMap: Record<ArenaTableRail, ArenaTableRail> = {
        wood: 'gold',
        gold: 'bronze',
        bronze: 'dark',
        dark: 'floral',
        floral: 'wood',
      };
      const next: ArenaTableRail = nextMap[currentTableRail] || 'wood';
      currentTableRail = next;
      localStorage.setItem('atl_table_rail', next);
      window.dispatchEvent(new Event('atl_table_rail_change'));
      arenaAudio.playCardSelect();
    } catch {}
  }, []);

  const handleToggleBooksToSet = React.useCallback(() => {
    try {
      const next = !currentShowBooksToSet;
      currentShowBooksToSet = next;
      localStorage.setItem('atl_show_books_to_set', String(next));
      window.dispatchEvent(new Event('atl_show_books_to_set_change'));
      arenaAudio.playCardSelect();
    } catch {}
  }, []);

  const [showCardHints, setShowCardHints] = React.useState<boolean>(currentShowCardHints);

  React.useEffect(() => {
    ensureCardHintsListener();
    cardHintsListeners.add(setShowCardHints);
    return () => {
      cardHintsListeners.delete(setShowCardHints);
    };
  }, []);

  const handleCycleSouthHandFontSize = React.useCallback(() => {
    try {
      const nextMap: Record<SouthHandFontSize, SouthHandFontSize> = {
        regular: 'medium',
        medium: 'extra-large',
        'extra-large': 'regular',
      };
      const next: SouthHandFontSize = nextMap[currentSouthHandFontSize] || 'medium';
      currentSouthHandFontSize = next;
      localStorage.setItem('atl_south_hand_font_size', next);
      window.dispatchEvent(new Event('atl_south_hand_font_size_change'));
      arenaAudio.playCardSelect();
    } catch {}
  }, []);

  const handleToggleCardHints = React.useCallback(() => {
    try {
      const next = !currentShowCardHints;
      currentShowCardHints = next;
      localStorage.setItem('atl_card_hints', String(next));
      window.dispatchEvent(new Event('atl_card_hints_change'));
      arenaAudio.playCardSelect();
    } catch {}
  }, []);

  return {
    tableColor,
    handleCycleTableColor,
    tableRail,
    handleToggleTableRail,
    showBooksToSet,
    handleToggleBooksToSet,
    southHandFontSize,
    handleCycleSouthHandFontSize,
    showCardHints,
    handleToggleCardHints,
  };
}
